import React, { useMemo, createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JobStatus } from "services/jobStatuses/types/jobStatus";
import { JobType } from "services/jobStatuses/types/jobType";
import { JobsStatusesResponse } from "services/jobStatuses/types/jobsStatusesResponse";
import { useAuth } from "features/auth";
import { UserRoleEnum } from "features/users/types";

export interface WaitingOverlayOptions {
  text?: string;
  timeout?: number;
  jobType?: JobType;
  timeLeftToFinish?: string;
}

export interface IWaitContext {
  showWait: (text?: string, timeout?: number) => void;
  hideWait: () => void;
  showJobProgress: (job: JobsStatusesResponse | undefined) => void;
  hideJobProgress: () => void;
  isWaiting: boolean;
  waitingOverlay?: WaitingOverlayOptions;
}

export const WaitingContext = createContext<IWaitContext>(null!);

export const WaitingOverlayProvider = (props: React.PropsWithChildren<{}>) => {
  const [waitingOverlay, setWaitingOverlay] = useState<WaitingOverlayOptions>();
  const navigate = useNavigate();
  const auth = useAuth();

  const showWait = (text?: string, timeout?: number) => {
    setWaitingOverlay({
      text: text ?? "Please wait",
      timeout: timeout ?? 5000,
    } as WaitingOverlayOptions);
  };

  const getJobTypeString = (jobType: JobType | undefined): string => {
    if (auth.userRole > UserRoleEnum.Manager) {
      return "Please wait, while we work on something";
    }

    switch (jobType) {
      case JobType.FileInitialization:
        return "Initialization in progress";
      case JobType.FileSynchronization:
        return "Synchronization in progress";
      case JobType.FileCreation:
        return "File Creation in progress";
      case JobType.ProjectCompletionCalculation:
        return "Project Completion Calculation in progress";
      case JobType.LanguageSynchronization:
        return "Language Synchronization in progress";
      case JobType.StatisticsCreation:
        return "Statistics Creation in progress";
      case JobType.LanguageAdding:
        return "Adding new language in progress";
      case JobType.LanguageDeleting:
        return "Removing language in progress";
      case JobType.ProjectValidation:
        return "Project validation in progress";
      case JobType.DeleteWorkspace:
        return "Workspace deletion in progress";
      default:
        return `${jobType} in progress`;
    }
  };

  const getJobText = (job: JobsStatusesResponse) =>
    `${getJobTypeString(job.jobType)}.\nTime left: ${job?.timeLeftToFinish ?? "Almost there!"}`;

  const showJobProgress = (job: JobsStatusesResponse) => {
    const text = getJobText(job);

    setWaitingOverlay({
      text,
      timeout: 0,
      jobType: job.jobType,
      timeLeftToFinish: job.timeLeftToFinish,
    } as WaitingOverlayOptions);
  };

  const hideWait = (): void => {
    setWaitingOverlay(undefined);
  };

  const hideJobProgress = (): void => {
    setWaitingOverlay(currentOverlay => {
      if (currentOverlay?.jobType) {
        const text = getJobText({
          jobType: currentOverlay?.jobType,
          status: JobStatus.Success,
        } as JobsStatusesResponse);
        showWait(text, 2000);
      }

      if (currentOverlay) {
        // reload the page to refresh the data
        navigate(0);
      }

      return undefined; // Clears the overlay
    });
  };

  const contextValue = useMemo(
    () => ({
      showWait,
      showJobProgress,
      hideJobProgress,
      hideWait,
      waitingOverlay,
      isWaiting: waitingOverlay !== undefined,
    }),
    [waitingOverlay, showJobProgress, showWait],
  );

  const { children } = props;

  return <WaitingContext.Provider value={contextValue}>{children}</WaitingContext.Provider>;
};

export function useWaitingOverlay() {
  const context = useContext(WaitingContext);

  if (!context) {
    throw new Error("useWaitingOverlay must be used within the WaitingOverlayContextProvider");
  }

  return context;
}

export default WaitingOverlayProvider;
